import type { Spec, UIElement } from "@json-render/core";
import { normalizeM360ImageSrc } from "@/lib/m360Paths";
import type { M360StructuredBlock } from "../m360StructuredContent";
import { pgaIdFromPersonHref } from "../m360StructuredContent";

const PROFILE_PHOTO_MARKDOWN_PATTERN = /!\[profile photo\]\(([^)]+)\)/i;
const PROFILE_PHOTO_URL_LINE_PATTERN = /Profile photo URL:\s*(\S+)/i;

function photoUrlFromLine(line: string): string | null {
  const markdownMatch = line.match(PROFILE_PHOTO_MARKDOWN_PATTERN);
  if (markdownMatch?.[1]) {
    return markdownMatch[1].trim();
  }

  const labeledMatch = line.match(PROFILE_PHOTO_URL_LINE_PATTERN);
  return labeledMatch?.[1]?.trim() ?? null;
}

const PERSON_HREF_PATTERN = /\/people\/(\d+)\//;
const PGA_ID_TEXT_PATTERN = /PGA ID\s+(\d+)/i;
const PGA_ID_LOOKUP_WINDOW = 12;

function pgaIdFromLine(line: string): string | null {
  const hrefMatch = line.match(PERSON_HREF_PATTERN);
  if (hrefMatch) {
    return hrefMatch[1];
  }

  const idMatch = line.match(PGA_ID_TEXT_PATTERN);
  return idMatch ? idMatch[1] : null;
}

/** Find the nearest PGA ID within a few lines before/after a photo row. */
function pgaIdNearLine(lines: string[], index: number, assignedPhotos: Map<string, string>): string | null {
  const candidates: { id: string; distance: number; lineIndex: number }[] = [];
  const start = Math.max(0, index - PGA_ID_LOOKUP_WINDOW);
  const end = Math.min(lines.length - 1, index + PGA_ID_LOOKUP_WINDOW);

  for (let candidateIndex = start; candidateIndex <= end; candidateIndex += 1) {
    const candidateId = pgaIdFromLine(lines[candidateIndex]);
    if (!candidateId) {
      continue;
    }

    candidates.push({
      id: candidateId,
      distance: Math.abs(candidateIndex - index),
      lineIndex: candidateIndex
    });
  }

  if (candidates.length === 0) {
    return null;
  }

  const minDistance = Math.min(...candidates.map((candidate) => candidate.distance));
  let tied = candidates.filter((candidate) => candidate.distance === minDistance);

  const unassigned = tied.filter((candidate) => !assignedPhotos.has(candidate.id));
  if (unassigned.length > 0) {
    tied = unassigned;
  }

  tied.sort((left, right) => {
    const leftBeforePhoto = left.lineIndex < index;
    const rightBeforePhoto = right.lineIndex < index;
    if (leftBeforePhoto !== rightBeforePhoto) {
      return leftBeforePhoto ? -1 : 1;
    }

    return left.lineIndex - right.lineIndex;
  });

  return tied[0]?.id ?? null;
}

function countPersonCards(spec: Spec): number {
  return Object.values(spec.elements ?? {}).filter((element) => (element as UIElement).type === "PersonCard").length;
}

/** Map PGA ID (or a single-result fallback key) to a profile photo URL from tool markdown. */
export function extractProfilePhotoUrlsByPgaId(markdown: string): Map<string, string> {
  const photos = new Map<string, string>();
  if (!markdown.trim()) {
    return photos;
  }

  const lines = markdown.split("\n");

  for (let index = 0; index < lines.length; index += 1) {
    const url = photoUrlFromLine(lines[index]);
    if (!url) {
      continue;
    }

    let pgaId: string | null = pgaIdNearLine(lines, index, photos);

    if (pgaId) {
      photos.set(pgaId, url);
    } else if (!photos.has("__single__")) {
      photos.set("__single__", url);
    }
  }

  return photos;
}

function resolveProfilePhoto(
  photosByPgaId: Map<string, string>,
  pgaId?: string | null,
  options?: { allowSingleFallback?: boolean }
): string | null {
  if (pgaId && photosByPgaId.has(pgaId)) {
    return photosByPgaId.get(pgaId) ?? null;
  }

  if (options?.allowSingleFallback && photosByPgaId.has("__single__")) {
    const hasOtherKeyedPhotos = [...photosByPgaId.keys()].some((key) => key !== "__single__");
    if (!hasOtherKeyedPhotos) {
      return photosByPgaId.get("__single__") ?? null;
    }
  }

  if (!pgaId && photosByPgaId.size === 1) {
    return photosByPgaId.values().next().value ?? null;
  }

  return null;
}

function normalizedProfilePhoto(
  photosByPgaId: Map<string, string>,
  pgaId?: string | null,
  options?: { allowSingleFallback?: boolean }
): string | undefined {
  const resolved = resolveProfilePhoto(photosByPgaId, pgaId, options);
  return normalizeM360ImageSrc(resolved) ?? undefined;
}

function isPersonRecordProps(item: Record<string, unknown>): boolean {
  const kind = item.kind ? String(item.kind) : "";
  const href = item.href ? String(item.href) : "";
  return kind === "person" || href.startsWith("/people/");
}

function pgaIdFromRecordProps(item: Record<string, unknown>): string | null {
  if (item.pgaId) {
    return String(item.pgaId);
  }

  return pgaIdFromPersonHref(item.href ? String(item.href) : undefined);
}

function stateKeyFromPointer(pointer: string): string | null {
  const key = pointer.replace(/^\//, "").split("/")[0];
  return key || null;
}

function withItemPhoto(
  item: Record<string, unknown>,
  photosByPgaId: Map<string, string>,
  allowSingleFallback: boolean
): { next: Record<string, unknown>; changed: boolean } {
  if (item.imageUrl || !isPersonRecordProps(item)) {
    return { next: item, changed: false };
  }

  const imageUrl = normalizedProfilePhoto(photosByPgaId, pgaIdFromRecordProps(item), { allowSingleFallback });
  if (!imageUrl) {
    return { next: item, changed: false };
  }

  return { next: { ...item, imageUrl }, changed: true };
}

function countPersonRecords(spec: Spec): number {
  let count = countPersonCards(spec);

  for (const element of Object.values(spec.elements ?? {})) {
    const typedElement = element as UIElement;
    if (typedElement.type !== "RecordList") {
      continue;
    }

    const rawItems = typedElement.props?.items;
    if (Array.isArray(rawItems)) {
      count += rawItems.filter(
        (item) => item && typeof item === "object" && isPersonRecordProps(item as Record<string, unknown>)
      ).length;
      continue;
    }

    if (rawItems && typeof rawItems === "object" && "$state" in rawItems) {
      const stateKey = stateKeyFromPointer(String((rawItems as { $state: string }).$state));
      const stateItems = stateKey ? spec.state?.[stateKey] : undefined;
      if (Array.isArray(stateItems)) {
        count += stateItems.filter(
          (item) => item && typeof item === "object" && isPersonRecordProps(item as Record<string, unknown>)
        ).length;
      }
    }
  }

  return count;
}

/** Fill missing PersonCard and people RecordList imageUrl fields from ```tool-results``` profile photo markdown. */
export function mergeProfilePhotosIntoSpec(spec: Spec | null, markdown: string | null): Spec | null {
  if (!spec?.elements || !markdown?.trim()) {
    return spec;
  }

  const photosByPgaId = extractProfilePhotoUrlsByPgaId(markdown);
  if (photosByPgaId.size === 0) {
    return spec;
  }

  const allowSingleFallback = countPersonRecords(spec) === 1;
  let changed = false;
  const elements = { ...spec.elements };
  const state = spec.state ? { ...spec.state } : spec.state;

  for (const [key, element] of Object.entries(elements)) {
    const typedElement = element as UIElement;
    if (typedElement.type === "PersonCard") {
      const props = { ...(typedElement.props ?? {}) } as Record<string, unknown>;
      if (props.imageUrl) {
        continue;
      }

      const imageUrl = normalizedProfilePhoto(photosByPgaId, props.pgaId ? String(props.pgaId) : null, {
        allowSingleFallback
      });
      if (!imageUrl) {
        continue;
      }

      elements[key] = {
        ...typedElement,
        props: {
          ...props,
          imageUrl
        }
      };
      changed = true;
      continue;
    }

    if (typedElement.type !== "RecordList") {
      continue;
    }

    const rawItems = typedElement.props?.items;
    if (Array.isArray(rawItems)) {
      let itemsChanged = false;
      const nextItems = rawItems.map((item) => {
        if (!item || typeof item !== "object") {
          return item;
        }

        const result = withItemPhoto(item as Record<string, unknown>, photosByPgaId, allowSingleFallback);
        if (result.changed) {
          itemsChanged = true;
        }
        return result.next;
      });

      if (itemsChanged) {
        elements[key] = {
          ...typedElement,
          props: {
            ...(typedElement.props ?? {}),
            items: nextItems
          }
        };
        changed = true;
      }
      continue;
    }

    if (!rawItems || typeof rawItems !== "object" || !("$state" in rawItems) || !state) {
      continue;
    }

    const stateKey = stateKeyFromPointer(String((rawItems as { $state: string }).$state));
    const stateItems = stateKey ? state[stateKey] : undefined;
    if (!stateKey || !Array.isArray(stateItems)) {
      continue;
    }

    let itemsChanged = false;
    const nextItems = stateItems.map((item) => {
      if (!item || typeof item !== "object") {
        return item;
      }

      const result = withItemPhoto(item as Record<string, unknown>, photosByPgaId, allowSingleFallback);
      if (result.changed) {
        itemsChanged = true;
      }
      return result.next;
    });

    if (itemsChanged) {
      state[stateKey] = nextItems;
      changed = true;
    }
  }

  return changed ? { ...spec, elements, ...(state ? { state } : {}) } : spec;
}

/** Fill missing person card/list imageUrl fields from ```tool-results``` profile photo markdown. */
export function mergeProfilePhotosIntoBlock(
  block: M360StructuredBlock | null,
  markdown: string | null
): M360StructuredBlock | null {
  if (!block || !markdown?.trim()) {
    return block;
  }

  const photosByPgaId = extractProfilePhotoUrlsByPgaId(markdown);
  if (photosByPgaId.size === 0) {
    return block;
  }

  if (block.type === "person") {
    if (block.imageUrl) {
      return block;
    }

    const imageUrl = normalizedProfilePhoto(photosByPgaId, block.pgaId, { allowSingleFallback: true });
    return imageUrl ? { ...block, imageUrl } : block;
  }

  if (block.type !== "record_list") {
    return block;
  }

  let changed = false;
  const items = block.items.map((item) => {
    if (item.imageUrl) {
      return item;
    }

    const pgaId = pgaIdFromPersonHref(item.href ?? undefined);
    if (!pgaId) {
      return item;
    }

    const imageUrl = normalizedProfilePhoto(photosByPgaId, pgaId, { allowSingleFallback: false });
    if (!imageUrl) {
      return item;
    }

    changed = true;
    return { ...item, imageUrl };
  });

  return changed ? { ...block, items } : block;
}
